// Copyright (c) 2025 Audere, All Rights Reserved

import CloudUpload from "@mui/icons-material/CloudUpload";
import { Button, Paper, Typography } from "@mui/material";
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";
import { getFileProcessorWorker } from "../utils/workerUtils";
import type { FileInfo, FileUploadAreaProps, FileUploadAreaRef } from "../types/projects";
import { useTranslation } from "react-i18next";
import { createStyleSheet } from "../utils/styles";
import { theme } from "../../theme";

const FileUploadArea = forwardRef<FileUploadAreaRef, FileUploadAreaProps>(
  ({ setFiles, setProcessing }, ref) => {
    const { t } = useTranslation("FileUploadArea");
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      clearFiles: () => {
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        setFiles([]);
        setProcessing(false);
      },
    }));

    const handleDragOver = useCallback((event: React.DragEvent) => {
      event.preventDefault();
      setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((event: React.DragEvent) => {
      event.preventDefault();
      setIsDragging(false);
    }, []);

    const processFiles = useCallback(
      async (fileList: File[]) => {
        setProcessing(true);
        try {
          const worker = getFileProcessorWorker();
          const batchSize = 10;

          for (let i = 0; i < fileList.length; i += batchSize) {
            const batch = fileList.slice(i, i + batchSize);

            // Get current hashes to avoid duplicates
            let currentHashes: string[] = [];
            setFiles((prev: FileInfo[]) => {
              currentHashes = prev.map(f => f.hash);
              return prev;
            });

            const batchPromises = batch.map(async file => {
              const isSupported = file.type === "image/jpeg";

              if (!isSupported) {
                return {
                  file,
                  hash: "",
                  filename: file.name,
                  size: file.size,
                  status: "invalid" as const,
                  progress: 0,
                  error: "Unsupported file format",
                };
              }

              try {
                const hash = await worker.generateHash(file);
                // Skip if hash already exists
                if (currentHashes.includes(hash)) {
                  return null;
                }

                const newFile: FileInfo = {
                  file,
                  hash,
                  filename: file.name,
                  size: file.size,
                  status: "pending",
                  progress: 0,
                };
                return newFile;
              } catch (err) {
                console.error("Error processing file:", file.name, err);
                return {
                  file,
                  hash: "",
                  filename: file.name,
                  size: file.size,
                  status: "error" as const,
                  progress: 0,
                  error: "Failed to process file",
                };
              }
            });

            const batchResults = (await Promise.all(batchPromises)).filter(Boolean) as FileInfo[];
            setFiles((prev: FileInfo[]) => [...prev, ...batchResults]);

            await new Promise(resolve => setTimeout(resolve, 10));
          }
        } catch (err) {
          console.error("Error processing files:", err);
        } finally {
          setProcessing(false);
        }
      },
      [setFiles, setProcessing]
    );

    const handleFileSelect = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(event.target.files || []);
        if (selectedFiles.length > 0) {
          processFiles(selectedFiles);
        }
      },
      [processFiles]
    );
    const handleDrop = useCallback(
      (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragging(false);
        const droppedFiles = Array.from(event.dataTransfer.files);
        processFiles(droppedFiles);
      },
      [processFiles]
    );

    return (
      <Paper
        sx={{
          ...styles.uploadArea,
          borderColor: isDragging ? theme.palette.primary.main : theme.palette.grey[300],
          backgroundColor: isDragging ? theme.palette.action.hover : theme.palette.background.paper,
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <CloudUpload sx={styles.uploadIcon} />
        <Typography variant="h6" gutterBottom>
          {t("dragTextPrimary")}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t("dragTextSecondary")}
        </Typography>
        <Button
          variant="contained"
          onClick={() => fileInputRef.current?.click()}
          sx={styles.selectButton}
        >
          {t("selectFiles")}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={handleFileSelect}
          accept=".jpg,.jpeg"
          data-testid="hidden-file-input"
          
        />
      </Paper>
    );
  }
);

export default FileUploadArea;

const styles = createStyleSheet({
  uploadArea: {
    padding: "32px",
    textAlign: "center",
    border: "2px dashed",
    borderColor: theme.palette.grey[300],
    backgroundColor: theme.palette.background.paper,
    transition: "all 0.2s ease",
    marginBottom: "24px",
    mt: 2,
  },
  uploadIcon: {
    fontSize: 48,
    color: theme.palette.primary.main,
    marginBottom: "16px",
  },
  selectButton: {
    marginTop: "16px",
  },
});
